import type { TEntityId } from "./entity-id";
import type { IEntityOwner } from "./user";

export enum ERiskPriority {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

export enum ECoverage {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

// NOTE: Keep in sync with the backend enum in server/src/probes/types/probes.types.ts.
// "input" — moderate user messages (default)
// "retrieval" — moderate RAG chunks before they enter the main LLM prompt
// "all" — both paths
export enum EProbeAppliesTo {
  INPUT = "input",
  RETRIEVAL = "retrieval",
  ALL = "all",
}

export enum EProbeSortColumn {
  CREATED_AT = "createdAt",
  NAME = "name",
  RISK_PRIORITY = "riskPriority",
  COVERAGE = "coverage",
  PROBE_TYPE = "probeType",
  STATUS = "status",
}

export interface IProbeBehavior {
  id: TEntityId;
  uuid: string;
  probeId: TEntityId;
  behavior: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface IProbe {
  id: TEntityId;
  uuid: string;
  name: string;
  description: string | null;
  riskPriority: ERiskPriority;
  coverage: ECoverage;
  projectId: TEntityId | null;
  appliedModelUuids: string[] | null;
  appliedAssistantUuids: string[] | null;
  appliesTo: EProbeAppliesTo;
  isEnabled: boolean;
  sourceFileS3Key: string | null;
  sourceModelUuid: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  behaviors?: IProbeBehavior[];
  // NOTE: NSAI-3491 — the user who created the probe, rendered as the Owner
  // column. Null for probes created before owners were recorded, for the seeded
  // global probes, and once the owner's account is deleted.
  user?: IEntityOwner | null;
}

export interface IActiveProbe {
  id: TEntityId;
  uuid: string;
  name: string;
  description: string | null;
  riskPriority: ERiskPriority;
  coverage: ECoverage;
  userId: TEntityId | null;
  behaviors: IProbeBehavior[];
}

export interface ICreateProbeDto {
  name: string;
  description?: string | null;
  riskPriority: ERiskPriority;
  coverage: ECoverage;
  behaviors: {
    behavior: string;
  }[];
  appliedModelUuids?: string[] | null;
  appliedAssistantUuids?: string[] | null;
  appliesTo?: EProbeAppliesTo;
  isEnabled?: boolean;
  sourceFileS3Key?: string;
  sourceModelUuid?: string;
}

export interface IPresignProbeFileUploadParams {
  fileName: string;
  contentType: string;
}

export interface IPresignProbeFileUploadResponse {
  uploadUrl: string;
  s3Key: string;
}

export interface IGenerateProbeFromFileParams {
  s3Key: string;
  fileName: string;
  contentType: string;
  modelUuid: string;
  signal?: AbortSignal;
}

export interface IDownloadSourceFileParams {
  probeUuid: string;
  onProgress?: (event: { loaded: number; total: number | undefined }) => void;
}

export interface IGeneratedProbeBehavior {
  behavior: string;
}

export interface IGeneratedProbeData {
  name: string;
  description: string;
  riskPriority: ERiskPriority;
  coverage: ECoverage;
  behaviors: IGeneratedProbeBehavior[];
}

// NOTE: Enums for probe file upload step flow (shared between web and mobile)
export enum ECreationMethod {
  MANUAL = "manual",
  FILE_UPLOAD = "file-upload",
}

export enum ECreationStep {
  SELECT_METHOD = "select-method",
  FORM = "form",
  FILE_UPLOAD = "file-upload",
}

export enum EProbeFileUploadPhase {
  IDLE = "idle",
  UPLOADING = "uploading",
  PROCESSING = "processing",
  DONE = "done",
}

export enum EProbeUploadStep {
  SELECT_MODEL = 1,
  UPLOAD_FILE = 2,
  PROCESSING = 3,
}

export interface IGenerateProbeFromFileMutationParams {
  file: File;
  modelUuid: string;
  onSettled?: () => void;
}

export interface IUseGenerateProbeFromFileResult {
  generate: (params: IGenerateProbeFromFileMutationParams) => void;
  cancel: () => void;
  phase: EProbeFileUploadPhase;
  uploadProgress: number;
  data: IGeneratedProbeData | undefined;
  error: Error | null;
  s3Key: string | null;
}

export type TCheckboxCheckedState = boolean | "indeterminate";

export interface IProbeSelectChangeProps {
  uuid: string;
  isSelected: boolean;
}

export interface IBulkToggleProbePayload {
  uuids: string[];
  isEnabled: boolean;
}

export interface IBulkDeleteProbePayload {
  uuids: string[];
}

export interface IBulkProbeOperationResultItem {
  uuid: string;
  isSuccess: boolean;
  reason?: string;
}

export interface IBulkProbeOperationResult {
  results: IBulkProbeOperationResultItem[];
}

export interface IUpdateProbeDto {
  name?: string;
  description?: string | null;
  riskPriority?: ERiskPriority;
  coverage?: ECoverage;
  behaviors?: {
    uuid?: string;
    behavior: string;
  }[];
  appliedModelUuids?: string[] | null;
  appliedAssistantUuids?: string[] | null;
  appliesTo?: EProbeAppliesTo;
  isEnabled?: boolean;
  sourceFileS3Key?: string | null;
  sourceModelUuid?: string | null;
}

export type TProbeModelSlotKey =
  | "probe_validation"
  | "probe_rerouting"
  | "probe_retrieval"
  | "pii_intent_classifier"
  | "pii_trusted";

export type TProbeModelValueField =
  | "probeValidationModelUuid"
  | "probeReroutingModelUuid"
  | "probeRetrievalModelUuid"
  | "piiIntentClassifierModelUuid"
  | "piiTrustedModelUuid";

export type TProbeModelDefaultField =
  | "validationModel"
  | "reroutingModel"
  | "retrievalModel"
  | "piiIntentClassifierModel"
  | "piiTrustedModel";

// NOTE: One slot per configurable probe/PII model, consumed by the web and
// mobile Chat Settings pickers so both stay in step.
export interface IProbeModelSlot {
  key: TProbeModelSlotKey;
  testIdPrefix: string;
  valueField: TProbeModelValueField;
  defaultField: TProbeModelDefaultField;
  // NOTE: Hides the clear (X) button so the model cannot be deselected.
  isMandatory?: boolean;
  shouldConfirmClear?: boolean;
  // NOTE: Slot renders only when the PII intent routing feature flag is on.
  isPiiIntentRoutingSlot?: boolean;
  // NOTE: NSAI-7065 — the slot may hold a guard (moderation classifier) model.
  // Only probe validation runs one; every other slot needs a completion model.
  isGuardModelAllowed?: boolean;
}
